import {
  createContext,
  FC,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useListMyConnections, useSwitchAsGuest } from '#/api/successor';
import useAuth from '#/hooks/useAuth';
import useLocalStorage from '#/hooks/useLocalStorage';

type CurrentAccount = {
  [key: string]: any;
};

type AccSwitchContextProps = {
  currentAccount: CurrentAccount | null;
  handleSwitchAccount: (id: number) => void;
  isSwitching?: boolean;
};
type AccSwitchProviderProps = {
  children: ReactNode;
};

const AccountSwitchContext = createContext<AccSwitchContextProps>({
  currentAccount: null,
  handleSwitchAccount: () => {},
  isSwitching: false,
});

const AccountSwitchProvider: FC<AccSwitchProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();

  const [currentAccount, setCurrentAccount] = useState<CurrentAccount | null>(
    user
  );
  const [guestOn, setLocalStorageGuestOn, removeGuestOn] = useLocalStorage(
    'guest_on',
    null
  );

  const [, , removeOwnerId] = useLocalStorage('owner_id', null);
  const [, , removeOwnerID] = useLocalStorage('ownerID', null);

  const [searchParams] = useSearchParams();

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data: accountsList } = useListMyConnections(!!user);
  const { mutateAsync: switchToAccount, isLoading: isSwitching } =
    useSwitchAsGuest();

  const handleSwitchAccount = async (id: number) => {
    if (!user && !isAuthenticated) return;
    if (id === user?.id) {
      setCurrentAccount(user);
      removeGuestOn();
      removeOwnerID();
      removeOwnerId();
    } else {
      try {
        const response = await switchToAccount({ id });
        setLocalStorageGuestOn(id);
        setCurrentAccount(response?.data);
        if (!searchParams.get('invitation_id')) {
          navigate(`/dashboard/gast-bei/${id}`);
        }
      } catch (error) {
        console.error('Error switching account: ', error);
      }
    }
  };

  useEffect(() => {
    if (guestOn && !pathname.includes('passwort-reset-bestaetigen')) {
      handleSwitchAccount(guestOn);
    } else if (
      user &&
      !pathname.includes('passwort-reset-bestaetigen') &&
      !guestOn
    ) {
      handleSwitchAccount(user.id);
    }
  }, [guestOn, user]);

  useEffect(() => {
    if (
      !user?.primary_account &&
      accountsList?.length &&
      !pathname.includes('passwort-reset-bestaetigen')
    ) {
      handleSwitchAccount(accountsList[0].id);
    }
  }, [user, accountsList]);

  // useEffect(() => {
  //   if (!searchParams.get('invitation_id') && !pathname.includes('gast-bei')) {
  //     removeGuestOn();
  //   }
  // }, [pathname, searchParams]);

  // useEffect(() => {
  //   if (
  //     currentAccount?.id &&
  //     currentAccount?.id !== user?.id &&
  //     isAuthenticated
  //   ) {
  //     navigate(`/dashboard/gast-bei/${currentAccount?.id}`);
  //   }
  // }, [currentAccount, user, isAuthenticated]);

  // useEffect(() => {
  //   if (!location.pathname.includes('/dashboard/gast-bei') && guestOn) {
  //     removeGuestOn();
  //   }
  // }, [guestOn]);

  const values = useMemo(
    () => ({ currentAccount, handleSwitchAccount, isSwitching }),
    [handleSwitchAccount]
  );

  return (
    <AccountSwitchContext.Provider value={values}>
      {children}
    </AccountSwitchContext.Provider>
  );
};

export { AccountSwitchProvider, AccountSwitchContext };
